/**
 * 基本的なセマフォの実装
 *
 * 指定された数のパーミットを管理し、リソースへの同時アクセス数を制限するために使用する
 * パーミットが利用可能な場合は即座に取得でき、利用できない場合はキューに入る
 */
export class BasicSemaphore {
  /** 最大パーミット数 */
  #maxPermits: number
  /** 現在利用可能なパーミットの数 */
  #permits: number
  /** パーミット待ちの関数キュー */
  #queue: Array<() => void> = []

  /**
   * セマフォのインスタンスを作成
   *
   * @param permits - 初期パーミット数
   */
  constructor(permits: number) {
    this.#maxPermits = permits
    this.#permits = permits
  }

  /**
   * セマフォからパーミットを取得
   *
   * 利用可能なパーミットがある場合は即座に取得し、
   * ない場合は利用可能になるまで待機する
   *
   * @returns パーミットが取得できたときにresolveするPromise
   */
  async acquire(): Promise<void> {
    if (this.#permits > 0) {
      this.#permits -= 1
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      this.#queue.push(() => {
        this.#permits -= 1
        resolve()
      })
    })
  }

  /**
   * セマフォにパーミットを返却
   *
   * パーミットを返却し、待機中のタスクがあれば次のタスクを実行する
   */
  release(): void {
    this.#permits = Math.min(this.#permits + 1, this.#maxPermits)
    const next = this.#queue.shift()
    if (next !== undefined) {
      next()
    }
  }

  /**
   * 現在利用可能なパーミット数を取得
   *
   * @returns 現在利用可能なパーミット数
   */
  getPermits(): number {
    return this.#permits
  }
}
